import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import FinalCTA from "@/components/landing/FinalCTA";
import SEO, { SITE_URL } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { API } from "@/lib/api";
import { BLOG_ARTICLES } from "@/data/blogArticles";
import { buildArticleSchema, buildBreadcrumbSchema } from "@/lib/seoSchemas";
import { BLOG } from "@/constants/testIds";

export default function BlogArticle() {
  const { slug } = useParams();
  const { language } = useI18n();
  const siteUrl = SITE_URL;
  const staticArticle = BLOG_ARTICLES.find((a) => a.slug === slug);
  // Articles dynamiques (générés quotidiennement côté serveur) : fetch si pas dans les statiques.
  const [dynArticle, setDynArticle] = React.useState(undefined);
  React.useEffect(() => {
    if (staticArticle) return;
    setDynArticle(undefined);
    fetch(`${API}/blog/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setDynArticle(d))
      .catch(() => setDynArticle(null));
  }, [slug, staticArticle]);

  const article = staticArticle || dynArticle;
  if (!staticArticle && dynArticle === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" data-testid="blog-article-loading" />
      </div>
    );
  }
  if (!article) return <Navigate to="/blog" replace />;

  const content = article[language] || article.fr;
  const url = `${siteUrl}/blog/${slug}`;
  const sameCocon = BLOG_ARTICLES.filter((a) => a.slug !== slug && a.cocon === article.cocon);
  const others = BLOG_ARTICLES.filter((a) => a.slug !== slug && a.cocon !== article.cocon);
  const related = [...sameCocon, ...others].slice(0, 3);
  const isEn = language === "en";

  const jsonLd = [
    buildArticleSchema({
      headline: content.title,
      description: content.metaDescription,
      url,
      image: `${siteUrl}/icon-512.png`,
      datePublished: article.publishedDate,
    }),
    buildBreadcrumbSchema(
      [{ name: isEn ? "Home" : "Accueil", path: "/" }, { name: "Blog", path: "/blog" }, { name: content.title, path: `/blog/${slug}` }],
      siteUrl
    ),
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO title={`${content.title} | Kalypso Music`} description={content.metaDescription} path={`/blog/${slug}`} jsonLd={jsonLd} />
      <Navbar />
      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">{isEn ? "Home" : "Accueil"}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground/80">{content.title}</span>
        </nav>

        <h1 className="mt-6 font-heading text-3xl font-medium leading-tight tracking-tight sm:text-4xl">{content.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{new Date(article.publishedDate).toLocaleDateString(isEn ? "en-US" : "fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{content.readingTime} {isEn ? "min read" : "min de lecture"}</span>
        </div>

        <article className="prose prose-invert mt-10 max-w-none">
          {content.body.map((block, i) => {
            if (block.type === "h2") return <h2 key={i} className="mt-8 font-heading text-xl font-medium tracking-tight sm:text-2xl">{block.text}</h2>;
            if (block.type === "ul") return (
              <ul key={i} className="mt-4 list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {block.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            );
            if (block.type === "h3") return <h3 key={i} className="mt-6 font-heading text-base font-semibold sm:text-lg">{block.text}</h3>;
            if (block.type === "audio") return <ArticleAudio key={i} block={block} isEn={isEn} />;
            if (block.type === "extlink") return (
              <p key={i} className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {block.before} <a href={block.href} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">{block.anchor}</a>{block.after}
              </p>
            );
            if (block.type === "link") return (
              <p key={i} className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {block.before} <Link to={block.to} className="font-medium text-primary hover:underline">{block.anchor}</Link>{block.after}
              </p>
            );
            return <p key={i} className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{block.text}</p>;
          })}
        </article>

        <div className="mt-12 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center">
          <p className="text-sm text-muted-foreground sm:text-base">{isEn ? "Ready to create your own personalized song?" : "Prêt à créer votre propre chanson personnalisée ?"}</p>
          <Link to="/create" className="mt-4 inline-block">
            <Button data-testid={BLOG.articleCta} className="rounded-full px-8">{isEn ? "Try Kalypso Music for free" : "Essayez Kalypso Music gratuitement"}</Button>
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading text-lg font-medium tracking-tight">{isEn ? "Also read" : "À lire aussi"}</h2>
            <div className="mt-4 space-y-3">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={BLOG.relatedLink(a.slug)}
                  className="block rounded-xl border border-border bg-card p-4 text-sm text-foreground/90 transition-colors hover:border-primary/40 hover:text-primary"
                >
                  {(a[language] || a.fr).title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
      <FinalCTA />
      <Footer />
    </div>
  );
}

// Lecteur des chansons d'exemple du blog (vraies créations Kalypso Music,
// servies par /api/public/blog-song/{id}/audio).
function ArticleAudio({ block, isEn }) {
  const [playing, setPlaying] = React.useState(false);
  const [prog, setProg] = React.useState({ c: 0, d: 0 });
  const ref = React.useRef(null);
  const fmt = (s) => (!s || !isFinite(s) ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`);
  React.useEffect(() => () => { ref.current?.pause(); }, []);
  const toggle = () => {
    if (playing) { ref.current?.pause(); setPlaying(false); return; }
    if (!ref.current) {
      const a = new Audio(`${API}/public/blog-song/${block.id}/audio`);
      a.ontimeupdate = () => setProg({ c: a.currentTime, d: a.duration || 0 });
      a.onended = () => setPlaying(false);
      ref.current = a;
    }
    ref.current.play().catch(() => {});
    setPlaying(true);
  };
  return (
    <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid={`article-audio-${block.id}`}>
      <div className="flex items-center gap-4">
        <button onClick={toggle} aria-label={playing ? "Pause" : "Play"} data-testid={`article-audio-toggle-${block.id}`}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow transition-transform hover:scale-105">
          {playing ? "❚❚" : "▶"}
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-sm font-semibold">{block.title}</p>
          <p className="text-xs text-muted-foreground">{block.genre} · {block.lang}</p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: prog.d ? `${(prog.c / prog.d) * 100}%` : "0%" }} />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>{fmt(prog.c)}</span><span>{fmt(prog.d)}</span>
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">{isEn ? "This song was created with Kalypso Music." : "Cette chanson a été créée avec Kalypso Music."}</p>
        <Link to="/create"><Button size="sm" className="rounded-full text-xs">{isEn ? "Create my song" : "Créer ma chanson"}</Button></Link>
      </div>
    </div>
  );
}
